import { MeshGradient } from '@paper-design/shaders-react'
import { useEffect, useState } from 'react'
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion'
import { useTheme } from '@/providers/ThemeProvider'

/**
 * The app's ambient ground: a slow, living field behind the page content.
 *
 * A WebGL shader (`MeshGradient`, @paper-design/shaders-react) distorts and swirls a
 * colour field per frame on the GPU. CSS gradients cannot churn like this — a moving
 * field needs a shader.
 *
 * Every value here is ported from the lantern ground of a reference implementation
 * in another of our codebases — "the reference", throughout this file — taking its
 * cool slide: the palette, the mesh shape, and — the part that actually decides how
 * it reads — the envelope it is sat in. Ported as a set. The numbers are
 * interdependent (a saturated palette only works because the bloom clears the
 * middle; the low opacity only works because the colours are saturated), so
 * changing one in isolation is how this turns to mud.
 */

/**
 * The cool set, both themes, exactly as the reference ships them.
 *
 * Deep teal (which happens to sit in our own primary's neighbourhood) → cyan →
 * indigo → a pale end. Saturated rather than pre-diluted into pastels: a pale
 * palette blurs to grey, while a saturated one held at a low opacity keeps its hue
 * exactly where it survives.
 *
 * Both themes carry hue. The reference does not fall back to greys on ink — its dark
 * set is the same three hues taken down to near-black, with the pale end replaced by
 * a deep blue-black.
 */
const COLORS = {
  light: ['#0f766e', '#06b6d4', '#6366f1', '#ecfeff'],
  dark: ['#134e4a', '#0e7490', '#312e81', '#0a1424'],
} as const

/**
 * The field's shape. One shape for both themes — the reference varies it per
 * announcement slide, and this is its cool slide's.
 */
const MESH = {
  distortion: 0.85,
  swirl: 0.62,
  scale: 1.2,
  offsetX: 0.2,
  offsetY: -0.18,
  rotation: 108,
  /** Grain gives the field its texture; without it the bands read as a gradient
   *  rather than as a surface. Fixed in the reference, not per-slide. */
  grainMixer: 0.12,
  grainOverlay: 0.12,
  speed: 0.45,
} as const

/**
 * How hard the field reads through the page.
 *
 * ── The one number that cannot be copied from the reference ──────────────────
 * There, the whole ground lives inside the HERO's box — a few hundred pixels of
 * greeting + composer, with the rest of the page plain beneath it. Ours is the app's
 * ambient ground: it spans the entire content area on every route. The reference's
 * percentages therefore describe a canvas several times larger here, and its ink
 * figure (0.45, calibrated against a bounded hero) covered our whole page in colour.
 *
 * Paper is the reference's 0.3 unchanged — the bloom already does the containing
 * there. Ink sits a little above it: its colours are the same hues taken down to
 * near-black, and at 0.28 under its own bloom the field vanished into the ground
 * entirely (there was nothing to see in dark mode). 0.4 is where it reads as a
 * faint depth again without becoming a wash.
 */
const OPACITY = { light: 0.3, dark: 0.4 } as const

/**
 * Softer on paper: on a pale ground the mesh's bands hold their edges and read as
 * banding rather than as light, and blurring takes the edges off without touching
 * the colours. Ink needs less, but still more than the reference's 5px — over a
 * page-sized canvas its bands are correspondingly larger, and at that size a 5px
 * blur leaves them reading as shapes rather than as depth.
 */
const BLUR = { light: 'blur(15px)', dark: 'blur(10px)' } as const

/**
 * How far the field reaches before it dissolves. ONE mask for both themes: on paper
 * the middle is cleared by the bloom below, which is what decides where colour
 * survives, so the mask does not have to differ.
 */
const MASK = 'radial-gradient(58% 62% at 50% 46%, #000 12%, transparent 72%)'

/**
 * The page's own background painted back over the field's middle, as an ellipse.
 *
 * This is what makes the ground read as light rather than as a curtain: the content
 * zone sits on clean ground, and the colour survives *past the ellipse's edges*,
 * exactly where nothing has to be read. An ellipse rather than a band on purpose —
 * colour leaking past its left and right edges is what makes the ground read as
 * light the content stands in front of, not a curtain dropped over it.
 *
 * PAPER is the reference's, verbatim: anchored high, so colour survives low and to
 * the sides of the greeting.
 *
 * INK has no counterpart in the reference — there, ink deliberately keeps the full
 * field, because the hero's own box already confined it to a band at the top of the
 * page. Our field has no such box; without a bloom the whole page fills with colour
 * (a navy wash behind the header, a teal one under the fold). So ink gets one too,
 * wider and centred lower than paper's: it has more page to clear, and its content
 * is spread down the whole of it rather than gathered in a hero. Not TOO wide,
 * though: the mask above dissolves the field by 72% of a 58%×62% ellipse, so a
 * bloom that clears out past that (the earlier 80%×84%, opaque to 44%) covered
 * every pixel the mask let through and dark mode showed no field at all. The bloom
 * has to end inside the mask's reach for any colour to survive between them.
 */
const BLOOM = {
  light:
    'radial-gradient(62% 64% at 50% 28%, var(--background) 0%, var(--background) 52%, transparent 78%)',
  dark: 'radial-gradient(66% 70% at 50% 44%, var(--background) 0%, var(--background) 30%, transparent 72%)',
} as const

/**
 * The field bleeds past its box on purpose, so its edges are never on screen. Both
 * layers carry the SAME bleed so the bloom tracks the ground exactly at any size.
 */
const BLEED = '-inset-x-[8%] -inset-y-[30%] md:-inset-x-[14%] md:-inset-y-[45%]'

export function SpaceMesh() {
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'
  /**
   * The field STAYS — reduced motion is a contract, not a kill switch
   * (docs/ui-effects.md §10). Only its drift stops, so the ground keeps being a
   * ground and nothing moves. The reference does exactly this.
   */
  const reduceMotion = usePrefersReducedMotion()
  /** A machine without WebGL gets a flat gradient of the same colours. */
  const [lowPerf, setLowPerf] = useState(false)

  useEffect(() => {
    try {
      if (!document.createElement('canvas').getContext('webgl2')) setLowPerf(true)
    } catch {
      setLowPerf(true)
    }
  }, [])

  const theme = isDark ? 'dark' : 'light'
  const colors = [...COLORS[theme]]

  return (
    // The clipping frame sits at exactly inset-0; the oversized field lives inside
    // it, so the bleed is painted but never scrollable.
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      style={{ contain: 'layout paint' }}
    >
      <div
        className={`absolute ${BLEED}`}
        style={{
          opacity: OPACITY[theme],
          filter: BLUR[theme],
          maskImage: MASK,
          WebkitMaskImage: MASK,
        }}
      >
        {lowPerf ? (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(120deg, ${colors[0]}, ${colors[1]} 45%, ${colors[2]})`,
            }}
          />
        ) : (
          <MeshGradient
            colors={colors}
            distortion={MESH.distortion}
            swirl={MESH.swirl}
            scale={MESH.scale}
            offsetX={MESH.offsetX}
            offsetY={MESH.offsetY}
            rotation={MESH.rotation}
            grainMixer={MESH.grainMixer}
            grainOverlay={MESH.grainOverlay}
            speed={reduceMotion ? 0 : MESH.speed}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </div>
      {/* The order is colour, then bloom, then every element — page content stacks
          above this frame entirely. */}
      <div className={`absolute ${BLEED}`} style={{ background: BLOOM[theme] }} />
    </div>
  )
}
